import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout/PageContainer';
import { Toast } from '@/components/common/Toast';
import { supabase } from '@/lib/supabase';
import { useSellerSignupData } from '@/hooks/useSellerSignupData';

export function SellerSignupCompletePage() {
  const navigate = useNavigate();
  const { accountInfo, businessInfo, storeInfo, agreements, clearAllData } = useSellerSignupData();

  const [isRegistering, setIsRegistering] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });
  const [countdown, setCountdown] = useState(3);
  
  // React Strict Mode로 인한 중복 실행 방지
  const hasProcessed = useRef(false);

  useEffect(() => {
    const processRegistration = async () => {
      // 중복 실행 방지 체크
      if (hasProcessed.current) {
        console.log('이미 처리된 요청입니다. 중복 실행을 방지합니다.');
        return;
      }
      hasProcessed.current = true;

      // localStorage에서 데이터 유효성 검사
      if (!accountInfo.email || !businessInfo.companyName || !storeInfo.storeName) {
        setToast({ show: true, message: '회원가입 정보가 없습니다. 처음부터 다시 시작해주세요.' });
        navigate('/signup/seller', { replace: true });
        return;
      }

      try {
        // 1. 비밀번호 해싱 (Edge Function 호출)
        const tempPassword = sessionStorage.getItem('temp_signup_password');
        if (!tempPassword) {
          throw new Error('비밀번호 정보가 없습니다. 처음부터 다시 시작해주세요.');
        }

        console.log('비밀번호 해싱 중...');
        const { data: hashData, error: hashError } = await supabase.functions.invoke('hash-password', {
          body: { password: tempPassword }
        });

        if (hashError || !hashData?.success) {
          console.error('비밀번호 해싱 실패:', hashError || hashData?.error);
          throw new Error('비밀번호 처리 중 오류가 발생했습니다.');
        }

        const passwordHash = hashData.hash;
        console.log('비밀번호 해싱 완료');

        // 2. 주소를 좌표로 변환 (기존 geocode Edge Function 활용)
        const fullAddress = `${storeInfo.address} ${storeInfo.detailAddress || ''}`.trim();
        console.log('주소를 좌표로 변환 중...');
        
        let latitude = null;
        let longitude = null;
        
        const { data: geoData, error: geoError } = await supabase.functions.invoke('geocode', {
          body: { address: fullAddress }
        });
        
        if (geoError) {
          console.error('Geocode Edge Function 에러:', geoError);
        }
        
        if (geoData?.success && geoData?.data) {
          latitude = geoData.data.latitude;
          longitude = geoData.data.longitude;
          console.log('좌표 변환 성공:', { latitude, longitude });
        } else {
          console.warn('좌표 변환 실패, 주소만 저장:', fullAddress);
          // 좌표 변환 실패해도 회원가입은 계속 진행
        }

        // 3. 판매자 신청서 생성 (seller_applications 테이블)
        // Auth 계정 없이 모든 정보를 저장
        const { error: applicationError } = await supabase
          .from('seller_applications')
          .insert({
            // user_id는 null (관리자 승인 후 채워짐)
            
            // Account Info
            email: accountInfo.email,
            password_hash: passwordHash, // 해시된 비밀번호 저장
            name: accountInfo.name,
            phone: accountInfo.phone,
            
            // Business Info
            company_name: businessInfo.companyName,
            representative: businessInfo.representative,
            business_number: businessInfo.businessNumber,
            bank_name: businessInfo.bankName,
            account_number: businessInfo.accountNumber,
            account_holder: businessInfo.accountHolder,
            
            // Store Info
            store_name: storeInfo.storeName,
            store_phone: storeInfo.storePhone,
            address: storeInfo.address,
            detail_address: storeInfo.detailAddress || null,
            food_types: storeInfo.foodTypes || [],
            custom_food_type: storeInfo.customFoodType || null,
            representative_menus: storeInfo.representativeMenus || [],
            operating_hours: storeInfo.operatingHours || null, // JSONB 타입이므로 직접 저장
            
            // 좌표 정보 추가
            latitude: latitude,
            longitude: longitude,
            
            // Agreements
            terms_agreed: agreements.terms || false,
            privacy_agreed: agreements.privacy || false,
            marketing_consent: agreements.marketing || false,
            
            status: 'PENDING'
          });

        if (applicationError) {
          console.error('Application creation error:', applicationError);
          throw new Error('판매자 신청서 저장에 실패했습니다.');
        }
        
        // 3. 회원가입 성공 시 데이터 정리
        clearAllData();
        // 임시 비밀번호 정보 정리
        sessionStorage.removeItem('temp_signup_password');

        setIsSuccess(true);
        setToast({ show: true, message: '회원가입 신청이 완료되었습니다! 관리자 승인을 기다려주세요.' });
        
        // 카운트다운 시작
        let timeLeft = 3;
        const countdownInterval = setInterval(() => {
          timeLeft--;
          setCountdown(timeLeft);
          
          if (timeLeft <= 0) {
            clearInterval(countdownInterval);
            navigate('/login'); // 로그인 페이지로 이동 (판매자는 아직 로그인 불가)
          }
        }, 1000);
      } catch (error) {
        console.error('Registration error:', error);
        
        // 에러 메시지 개선 및 상세 처리
        let errorMessage = '회원가입 중 오류가 발생했습니다.';
        let shouldRedirectToStart = false;
        
        if (error instanceof Error) {
          if (error.message.includes('비밀번호')) {
            errorMessage = error.message;
            shouldRedirectToStart = true;
          } else if (error.message.includes('판매자 신청서')) {
            errorMessage = '판매자 신청서 저장에 실패했습니다. 다시 시도해주세요.';
          } else {
            errorMessage = `회원가입 실패: ${error.message}`;
          }
        }
        
        setToast({ show: true, message: errorMessage });
        setIsSuccess(false);
        
        // 인증 문제인 경우 시작 페이지로 리다이렉트
        if (shouldRedirectToStart) {
          setTimeout(() => {
            navigate('/signup/seller', { replace: true });
          }, 3000);
        }
      } finally {
        setIsRegistering(false);
      }
    };

    processRegistration();
  }, []); // 컴포넌트 마운트 시 한 번만 실행


  const handleGoToLogin = () => {
    // 로그인 페이지로 이동 (판매자는 승인 후 로그인 가능)
    navigate('/login');
  };

  if (isRegistering) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#51C854] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-medium text-[#222222]">회원가입을 처리 중입니다...</p>
          <p className="text-sm text-[#666666] mt-2">잠시만 기다려주세요</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <PageContainer>
        <div className="flex flex-col items-center justify-center min-h-screen">
          {isSuccess ? (
            <>
              {/* 성공 아이콘 */}
              <div className="w-20 h-20 bg-[#51C854] rounded-full flex items-center justify-center mb-8">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="2"/>
                </svg>
              </div>

              {/* 성공 메시지 */}
              <h1 className="text-2xl font-bold text-[#222222] mb-4 text-center">
                회원가입이 완료되었습니다!
              </h1>
              
              <div className="text-center mb-8">
                <p className="text-base text-[#666666] mb-2">
                  판매자 계정이 성공적으로 생성되었습니다.
                </p>
                <p className="text-base text-[#666666] mb-2">
                  관리자 승인 후 서비스를 이용하실 수 있습니다.
                </p>
                <p className="text-sm text-[#999999] mb-2">
                  승인 결과는 등록하신 이메일로 안내드리겠습니다.
                </p>
                <p className="text-xs text-[#51C854] font-medium">
                  {countdown > 0 ? `${countdown}초 후 로그인 페이지로 이동합니다.` : '이동 중...'}
                </p>
              </div>

              {/* 버튼 영역 */}
              <div className="w-full max-w-[360px] space-y-4">
                <button
                  onClick={handleGoToLogin}
                  className="w-full h-[31px] bg-[#51C854] text-white font-semibold text-xl rounded-xl hover:bg-[#46B649] transition-colors"
                >
                  로그인 페이지로 가기
                </button>
              </div>
            </>
          ) : (
            <>
              {/* 실패 아이콘 */}
              <div className="w-20 h-20 bg-[#FF3B30] rounded-full flex items-center justify-center mb-8">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 6l12 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              {/* 실패 메시지 */}
              <h1 className="text-2xl font-bold text-[#222222] mb-4 text-center">
                회원가입에 실패했습니다
              </h1>
              
              <div className="text-center mb-8">
                <p className="text-base text-[#666666] mb-2">
                  회원가입 처리 중 오류가 발생했습니다.
                </p>
                <p className="text-base text-[#666666] mb-2">
                  이메일 인증 상태를 확인하거나 처음부터 다시 시도해주세요.
                </p>
                <p className="text-sm text-[#999999]">
                  문제가 지속될 경우 고객센터로 문의해주세요.
                </p>
              </div>

              {/* 버튼 영역 */}
              <div className="w-full max-w-[360px] space-y-4">
                <button
                  onClick={() => navigate('/signup/seller')}
                  className="w-full h-[31px] bg-[#51C854] text-white font-semibold text-xl rounded-xl hover:bg-[#46B649] transition-colors"
                >
                  다시 시도하기
                </button>
                
                <button
                  onClick={() => navigate('/login')}
                  className="w-full h-[31px] bg-[#DEDEDE] text-[#222222] font-semibold text-xl rounded-xl hover:bg-[#CECECE] transition-colors"
                >
                  로그인 페이지로 가기
                </button>
              </div>
            </>
          )}
        </div>
      </PageContainer>

      {/* 토스트 메시지 */}
      {toast.show && (
        <Toast
          message={toast.message}
          isVisible={toast.show}
          onClose={() => setToast({ show: false, message: '' })}
        />
      )}
    </div>
  );
}